/**
 * PlannerEmailCrewModal - Small ad-hoc email composer for the Crew Planner
 * context menu (EVENTSFLO-CREW-PLANNER-EMAIL-CREW-RESTORE-V1).
 *
 * Prefills subject + body from the shift context and lets the admin edit
 * before sending via POST /api/shifts/{shift_id}/crew/{staff_id}/email.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { sendShiftCrewEmail, extractErrorMessage } from "../../lib/api";

function formatTime(t) {
  if (!t) return "";
  return String(t).substring(0, 5);
}

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function buildDefaultSubject(shift) {
  if (!shift) return "Shift update";
  const when = shift.date ? formatDate(shift.date) : "";
  const name = shift.name || shift.project_name || "Shift";
  return `${name}${when ? ` — ${when}` : ""}`;
}

function buildDefaultBody(shift, staffName, venueLabel) {
  const lines = [];
  lines.push(`Hi ${staffName?.split(" ")?.[0] || "there"},`);
  lines.push("");
  lines.push("A quick note about your upcoming shift:");
  if (shift?.project_name) lines.push(`• Project: ${shift.project_name}`);
  if (shift?.name) lines.push(`• Shift: ${shift.name}`);
  if (shift?.date) lines.push(`• Date: ${formatDate(shift.date)}`);
  if (shift?.start_time || shift?.end_time) {
    lines.push(
      `• Time: ${formatTime(shift.start_time)}${shift.end_time ? ` – ${formatTime(shift.end_time)}` : ""}`,
    );
  }
  if (venueLabel) lines.push(`• Venue: ${venueLabel}`);
  if (shift?.meeting_place) lines.push(`• Meeting point: ${shift.meeting_place}`);
  lines.push("");
  lines.push("Please let me know if anything changes.");
  lines.push("");
  lines.push("Thanks!");
  return lines.join("\n");
}

export default function PlannerEmailCrewModal({
  open,
  onClose,
  shift,
  staffId,
  staffName,
  staffEmail,
  venueLabel,
}) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const missingEmail = !staffEmail;
  const defaultSubject = useMemo(() => buildDefaultSubject(shift), [shift]);
  const defaultBody = useMemo(
    () => buildDefaultBody(shift, staffName, venueLabel),
    [shift, staffName, venueLabel],
  );

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setMessage(defaultBody);
    }
  }, [open, defaultSubject, defaultBody]);

  const handleSend = async () => {
    if (!shift?.id || !staffId) {
      toast.error("Cannot send: missing shift or crew id.");
      return;
    }
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are required.");
      return;
    }
    setSending(true);
    try {
      await sendShiftCrewEmail(shift.id, staffId, {
        subject: subject.trim(),
        message: message,
      });
      toast.success(`Email sent to ${staffName || staffEmail}.`);
      onClose?.();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-lg"
        data-testid="planner-email-crew-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            Email {staffName || "crew member"}
          </DialogTitle>
          <DialogDescription>
            {missingEmail
              ? "Crew member has no email address."
              : `Sends to ${staffEmail}. The crew member sees your subject + message only.`}
          </DialogDescription>
        </DialogHeader>

        {!missingEmail && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="planner-email-subject" className="text-xs uppercase tracking-wide text-muted-foreground">
                Subject
              </Label>
              <Input
                id="planner-email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject"
                maxLength={300}
                data-testid="planner-email-subject-input"
              />
            </div>
            <div>
              <Label htmlFor="planner-email-message" className="text-xs uppercase tracking-wide text-muted-foreground">
                Message
              </Label>
              <Textarea
                id="planner-email-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={10}
                placeholder="Write your message…"
                data-testid="planner-email-message-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onClose?.()}
            disabled={sending}
            data-testid="planner-email-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={sending || missingEmail}
            data-testid="planner-email-send-btn"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
